import React, { useMemo } from "react";
import Calendar from "react-calendar";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface CustomCalendarProps {
  value: Date | string;
  onChange: (value: Date) => void;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  highlightedDates?: Date[];
  showToday?: boolean;
  locale?: string;
  backgroundColor?: string;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  value,
  onChange,
  className = "",
  minDate,
  maxDate,
  disabledDates = [],
  highlightedDates = [],
  showToday = true,
  locale = "pt-BR",
  backgroundColor,
}) => {
  const handleDateChange = (
    value: Value,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (value instanceof Date) {
      onChange(value);
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      value[0] instanceof Date
    ) {
      onChange(value[0]);
    }
  };

  // Verifica se uma data está desabilitada
  const isDateDisabled = ({ date }: { date: Date }) => {
    return disabledDates.some(
      (disabledDate) => date.toDateString() === disabledDate.toDateString(),
    );
  };

  // Adiciona classes customizadas aos tiles
  const getTileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== "month") return null;

    const classes: string[] = [];

    // Verifica se está destacado
    const isHighlighted = highlightedDates.some(
      (highlightedDate) =>
        date.toDateString() === highlightedDate.toDateString(),
    );

    if (isHighlighted) {
      classes.push("highlighted-date");
    }

    // Verifica se está desabilitado
    const isDisabled = disabledDates.some(
      (disabledDate) => date.toDateString() === disabledDate.toDateString(),
    );

    if (isDisabled) {
      classes.push("disabled-date");
    }

    return classes.join(" ");
  };

  // Adiciona conteúdo customizado aos tiles (ex: indicadores)
  const getTileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== "month") return null;

    const isHighlighted = highlightedDates.some(
      (highlightedDate) =>
        date.toDateString() === highlightedDate.toDateString(),
    );

    if (isHighlighted) {
      return <div className="tile-indicator" />;
    }

    return null;
  };

  const calendarValue = useMemo(() => {
    return typeof value === "string" ? new Date(value) : value;
  }, [value]);

  // Função para escurecer uma cor (para criar gradiente)
  const darkenColor = (color: string, percent: number): string => {
    const num = parseInt(color.replace("#", ""), 16);
    const r = Math.max(0, Math.min(255, (num >> 16) - percent));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) - percent));
    const b = Math.max(0, Math.min(255, (num & 0x0000ff) - percent));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  };

  const navigationBg = backgroundColor
    ? `linear-gradient(135deg, ${backgroundColor} 0%, ${darkenColor(backgroundColor, 30)} 100%)`
    : "linear-gradient(135deg, #85368c 0%, #85368c 100%)";

  const activeTileBg = backgroundColor
    ? `linear-gradient(135deg, ${backgroundColor} 0%, ${darkenColor(backgroundColor, 30)} 100%)`
    : "linear-gradient(135deg, #85368c 0%, #85368c 100%)";

  const activeTileBorder = backgroundColor
    ? darkenColor(backgroundColor, 30)
    : "#85368c";

  const activeTileHoverBg = backgroundColor
    ? `linear-gradient(135deg, ${darkenColor(backgroundColor, 30)} 0%, ${darkenColor(backgroundColor, 50)} 100%)`
    : "linear-gradient(135deg, #85368c 0%, #6b21a8 100%)";

  return (
    <div className={`custom-calendar-wrapper ${className}`}>
      <style>{`
        .custom-calendar-wrapper {
          position: relative;
          width: 100%;
          min-width: 0;
          overflow: hidden;
          box-sizing: border-box;
        }

        .custom-calendar {
          width: 100% !important;
          max-width: 100% !important;
          border: none;
          font-family: inherit;
          background: transparent;
          box-sizing: border-box;
        }

        /* Header de Navegação */
        .custom-calendar .react-calendar__navigation {
          display: flex;
          width: 100%;
          height: 40px;
          margin-bottom: 0.375rem;
          background: ${navigationBg};
          border-radius: 0.5rem;
          padding: 0.25rem;
          box-shadow: 0 2px 8px rgba(147, 51, 234, 0.15);
          box-sizing: border-box;
          overflow: hidden;
        }

        @media (min-width: 640px) {
          .custom-calendar .react-calendar__navigation {
            height: 48px;
            margin-bottom: 0.5rem;
            border-radius: 0.75rem;
          }
        }
        
        .custom-calendar .react-calendar__navigation button {
          color: white;
          min-width: 0;
          flex-shrink: 0;
          background: none;
          font-size: 13px;
          font-weight: 600;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
          padding: 0 6px;
          box-sizing: border-box;
        }

        @media (min-width: 640px) {
          .custom-calendar .react-calendar__navigation button {
            min-width: 0;
            font-size: 16px;
            padding: 0 8px;
          }
        }

        .custom-calendar .react-calendar__navigation button:enabled:hover,
        .custom-calendar .react-calendar__navigation button:enabled:focus {
          background-color: rgba(255, 255, 255, 0.15);
          transform: scale(1.05);
        }

        .custom-calendar .react-calendar__navigation button:enabled:active {
          transform: scale(0.95);
        }

        .custom-calendar .react-calendar__navigation button[disabled] {
          background-color: transparent;
          opacity: 0.4;
          cursor: not-allowed;
        }

        .custom-calendar .react-calendar__navigation__label {
          flex-grow: 1 !important;
          flex-shrink: 1 !important;
          min-width: 0 !important;
          font-weight: 700;
          font-size: 0.8rem;
          letter-spacing: 0.025em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        @media (min-width: 640px) {
          .custom-calendar .react-calendar__navigation__label {
            font-size: 1rem;
          }
        }

        /* Dias da Semana */
        .custom-calendar .react-calendar__month-view__weekdays {
          text-align: center;
          text-transform: uppercase;
          font-weight: 700;
          font-size: 0.6rem;
          color: #9333ea;
          margin-bottom: 0.375rem;
          letter-spacing: 0.03em;
          display: grid !important;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 2px;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }

        @media (min-width: 640px) {
          .custom-calendar .react-calendar__month-view__weekdays {
            font-size: 0.7rem;
            margin-bottom: 0.5rem;
          }
        }

        .dark .custom-calendar .react-calendar__month-view__weekdays {
          color: #a855f7;
        }

        .custom-calendar .react-calendar__month-view__weekdays__weekday {
          padding: 0.375rem 0;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 0;
          overflow: hidden;
        }

        @media (min-width: 640px) {
          .custom-calendar .react-calendar__month-view__weekdays__weekday {
            padding: 0.625rem 0;
          }
        }

        .custom-calendar .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none;
        }

        /* Grid dos Dias */
        .custom-calendar .react-calendar__month-view__days {
          display: grid !important;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 2px;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }

        /* View do mês - garantir que não vaze */
        .custom-calendar .react-calendar__month-view {
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }

        .custom-calendar .react-calendar__viewContainer {
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }

        /* Tiles dos Dias */
        .custom-calendar .react-calendar__tile {
          position: relative;
          width: 100%;
          max-width: 100%;
          aspect-ratio: 1;
          padding: 0;
          background: none;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          line-height: 1;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.2s ease;
          border: 2px solid transparent;
          cursor: pointer;
          box-sizing: border-box;
          overflow: hidden;
          min-width: 0;
        }

        @media (min-width: 640px) {
          .custom-calendar .react-calendar__tile {
            border-radius: 0.5rem;
            font-size: 0.875rem;
          }
        }

        .custom-calendar .react-calendar__month-view__days__day {
          color: #1F2937;
        }

        .dark .custom-calendar .react-calendar__month-view__days__day {
          color: #E5E7EB;
        }

        /* Hover States */
        .custom-calendar .react-calendar__tile:enabled:hover {
          background-color: #f3e8ff;
          border-color: #e9d5ff;
          transform: scale(1.05);
        }

        .dark .custom-calendar .react-calendar__tile:enabled:hover {
          background-color: rgba(147, 51, 234, 0.15);
          border-color: rgba(168, 85, 247, 0.3);
        }

        .custom-calendar .react-calendar__tile:enabled:focus {
          background-color: #e9d5ff;
          border-color: #9333ea;
          outline: none;
        }

        .dark .custom-calendar .react-calendar__tile:enabled:focus {
          background-color: rgba(147, 51, 234, 0.2);
          border-color: #a855f7;
        }

        /* Dia Atual */
        .custom-calendar .react-calendar__tile--now {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          font-weight: 700;
          border-color: #fbbf24;
          color: #92400e;
        }

        .dark .custom-calendar .react-calendar__tile--now {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.15) 100%);
          border-color: rgba(251, 191, 36, 0.4);
          color: #fcd34d;
        }

        .custom-calendar .react-calendar__tile--now:enabled:hover {
          background: linear-gradient(135deg, #fde68a 0%, #fcd34d 100%);
          border-color: #f59e0b;
        }

        /* Data Selecionada */
        .custom-calendar .react-calendar__tile--active {
          background: ${activeTileBg} !important;
          color: white !important;
          font-weight: 700;
          border-color: ${activeTileBorder} !important;
          box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
        }

        .custom-calendar .react-calendar__tile--active:enabled:hover {
          background: ${activeTileHoverBg} !important;
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(147, 51, 234, 0.4);
        }

        /* Meses Vizinhos */
        .custom-calendar .react-calendar__month-view__days__day--neighboringMonth {
          color: #d1d5db;
          opacity: 0.5;
        }

        .dark .custom-calendar .react-calendar__month-view__days__day--neighboringMonth {
          color: #6b7280;
        }

        /* Finais de Semana */
        .custom-calendar .react-calendar__month-view__days__day--weekend {
          color: #dc2626;
        }

        .dark .custom-calendar .react-calendar__month-view__days__day--weekend {
          color: #f87171;
        }

        /* Datas Destacadas */
        .custom-calendar .highlighted-date {
          background-color: #dbeafe;
          border-color: #3b82f6;
        }

        .dark .custom-calendar .highlighted-date {
          background-color: rgba(59, 130, 246, 0.15);
          border-color: rgba(59, 130, 246, 0.4);
        }

        .custom-calendar .highlighted-date:enabled:hover {
          background-color: #bfdbfe;
          border-color: #2563eb;
        }

        /* Indicador de Destaque */
        .tile-indicator {
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          background-color: #3b82f6;
          border-radius: 50%;
          box-shadow: 0 0 4px rgba(59, 130, 246, 0.6);
        }

        .dark .tile-indicator {
          background-color: #60a5fa;
          box-shadow: 0 0 4px rgba(96, 165, 250, 0.6);
        }

        /* Datas Desabilitadas */
        .custom-calendar .disabled-date {
          opacity: 0.3;
          cursor: not-allowed;
          background-color: #f3f4f6;
          pointer-events: none;
        }

        .dark .custom-calendar .disabled-date {
          background-color: rgba(75, 85, 99, 0.2);
        }

        /* Animações */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .custom-calendar .react-calendar__month-view__days {
          animation: fadeIn 0.3s ease-out;
        }

        /* Responsividade adicional para telas muito pequenas */
        @media (max-width: 360px) {
          .custom-calendar .react-calendar__tile {
            font-size: 0.65rem;
          }

          .custom-calendar .react-calendar__navigation {
            height: 36px;
          }

          .custom-calendar .react-calendar__navigation button {
            font-size: 11px;
            padding: 0 4px;
          }

          .custom-calendar .react-calendar__month-view__weekdays {
            font-size: 0.55rem;
            gap: 1px;
          }

          .custom-calendar .react-calendar__month-view__days {
            gap: 1px;
          }
        }

        /* Acessibilidade - Modo Alto Contraste */
        @media (prefers-contrast: high) {
          .custom-calendar .react-calendar__tile {
            border-width: 2px;
          }

          .custom-calendar .react-calendar__tile--active {
            border-width: 3px !important;
          }
        }

        /* Redução de Movimento */
        @media (prefers-reduced-motion: reduce) {
          .custom-calendar .react-calendar__tile,
          .custom-calendar .react-calendar__navigation button {
            transition: none;
          }

          .custom-calendar .react-calendar__month-view__days {
            animation: none;
          }
        }
      `}</style>
      <Calendar
        onChange={handleDateChange}
        value={calendarValue}
        className="custom-calendar"
        locale={locale}
        minDate={minDate}
        maxDate={maxDate}
        tileDisabled={disabledDates.length > 0 ? isDateDisabled : undefined}
        tileClassName={getTileClassName}
        tileContent={getTileContent}
        showNeighboringMonth={true}
        prevLabel={<FiChevronLeft size={20} />}
        nextLabel={<FiChevronRight size={20} />}
        prev2Label={null}
        next2Label={null}
        formatShortWeekday={(locale, date) => {
          const days = ["D", "S", "T", "Q", "Q", "S", "S"];
          return days[date.getDay()];
        }}
      />
    </div>
  );
};

export default CustomCalendar;
